import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";
import * as metadataService from "../../../services/metadataService";
import { ADDITIVE_AMOUNTS, amountBasis } from "../solutionText";

const CATEGORY_LABELS = {
    NANOPARTICLE: "nanoparticle", SALT: "salt", ACTIVE: "drug / active", FILLER: "filler",
    SURFACTANT: "surfactant", PLASTICIZER: "plasticiser", CROSSLINKER: "crosslinker", OTHER: "other",
};

const emptyAdditive = () => ({ additiveName: "", amount: "", amountUnit: "wt%", amountBasis: "polymer" });
const amountKey = (a) => `${a.amountUnit || "wt%"}|${amountBasis(a)}`;

/**
 * Additives of a setup: things added to the solution that are not polymers of
 * the fiber (nanoparticles, salts, drugs, fillers, ...). They never make a
 * record a "blend".
 */
export default function AdditiveModal({ additiveList, onClose, onSave }) {
    const [options, setOptions] = useState([]);
    const [additives, setAdditives] = useState(() =>
        Array.isArray(additiveList) && additiveList.length ? additiveList.map(a => ({ ...a, amount: a.amount ?? "" })) : []);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        metadataService.getAllAdditives()
            .then(data => setOptions((data || []).map(a => ({
                value: a.additiveName,
                label: `${a.additiveName}${a.fullName && a.fullName !== a.additiveName ? ` (${a.fullName})` : ''} · ${CATEGORY_LABELS[a.category] || 'other'}`,
            }))))
            .catch(err => console.error("Failed to load additives:", err));
    }, []);

    const update = (index, patch) => {
        setAdditives(prev => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
        setErrors(prev => prev.map((e, i) => (i === index ? {} : e)));
    };

    const validate = () => {
        const next = additives.map(a => {
            const err = {};
            if (!a.additiveName) err.additiveName = "Choose an additive";
            if (a.amount !== "" && a.amount !== null && Number(a.amount) <= 0) err.amount = "The amount must be greater than zero";
            return err;
        });
        setErrors(next);
        return !next.some(e => Object.keys(e).length);
    };

    const result = () => additives.map(a => ({
        additiveName: a.additiveName,
        amount: a.amount === "" || a.amount === null ? null : Number(a.amount),
        amountUnit: a.amount === "" || a.amount === null ? null : a.amountUnit || "wt%",
        amountBasis: a.amount === "" || a.amount === null ? null : amountBasis(a),
    }));

    const save = () => {
        if (validate()) {
            onSave(result());
            onClose();
        }
    };

    useModalKeyboardHandlers({ onClose, onSave: save, dependencies: [additives] });

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog" aria-modal="true" aria-labelledby="additive-modal-title">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal-content shadow-lg border-0" onSubmit={(e) => { e.preventDefault(); save(); }}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark" id="additive-modal-title">
                            <i className="bi bi-capsule me-2 text-primary"></i>
                            Additives
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body p-4 bg-light-subtle">
                        <p className="small text-muted">
                            Nanoparticles, salts, drugs and other actives, fillers, surfactants: anything in the solution that is not a polymer of the fiber.
                            Give the amount relative to what the paper does: the polymer, the whole solution, or the solvent (e.g. a salt dissolved in the solvent first).
                            Leave this empty if there are none.
                        </p>
                        {additives.map((a, index) => (
                            <div key={index} className="card border-light shadow-sm mb-3">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-bold text-primary">Additive #{index + 1}</span>
                                        <button type="button" className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                            onClick={() => { setAdditives(prev => prev.filter((_, i) => i !== index)); setErrors(prev => prev.filter((_, i) => i !== index)); }}>
                                            <i className="bi bi-trash me-1"></i> Remove
                                        </button>
                                    </div>
                                    <Select
                                        value={options.find(o => o.value === a.additiveName) || null}
                                        onChange={(selected) => update(index, { additiveName: selected?.value || "" })}
                                        options={options}
                                        placeholder="Search or select an additive..."
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                                        aria-label={`Additive ${index + 1}`}
                                    />
                                    {errors[index]?.additiveName && <div className="text-danger small mt-1">{errors[index].additiveName}</div>}
                                    <div className="input-group mt-3">
                                        <span className="input-group-text">Amount</span>
                                        <input type="number" step="any" min="0" className={`form-control ${errors[index]?.amount ? 'is-invalid' : ''}`}
                                            value={a.amount ?? ""} placeholder="optional"
                                            onChange={(e) => update(index, { amount: e.target.value })} aria-label={`Amount of additive ${index + 1}`} />
                                        <select className="form-select" style={{ maxWidth: '12rem' }} value={amountKey(a)}
                                            onChange={(e) => {
                                                const [amountUnit, basis] = e.target.value.split("|");
                                                update(index, { amountUnit, amountBasis: basis });
                                            }} aria-label={`Unit of additive ${index + 1}`}>
                                            {ADDITIVE_AMOUNTS.map(o => (
                                                <option key={`${o.unit}|${o.basis}`} value={`${o.unit}|${o.basis}`}>{o.unit} of {o.basis}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors[index]?.amount && <div className="text-danger small mt-1">{errors[index].amount}</div>}
                                </div>
                            </div>
                        ))}
                        <button type="button" className="btn btn-outline-primary w-100 py-2" style={{ borderStyle: 'dashed' }}
                            onClick={() => { setAdditives(prev => [...prev, emptyAdditive()]); setErrors(prev => [...prev, {}]); }}>
                            <i className="bi bi-plus-lg me-2"></i>Add an additive
                        </button>
                    </div>
                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
